'use client';

import { buildAuthHref } from '@/features/auth/utils/authRedirect';
import { getSafeAuthClientMessage } from '@/features/auth/utils/getSafeAuthClientMessage';
import { FormActions, FormField } from '@/features/forms/FormPrimitives';
import { clientFetch } from '@/lib/api/clientFetch';
import Link from 'next/link';
import { useState, useTransition } from 'react';

function readForgotPasswordMessage(messages, path, fallback = '') {
  const resolved = String(path || '')
    .split('.')
    .filter(Boolean)
    .reduce((current, segment) => current?.[segment], messages?.forgotPassword);

  return typeof resolved === 'string' ? resolved : fallback;
}

function buildLocalResetPath(resetUrl, redirectTarget) {
  if (typeof resetUrl !== 'string' || !resetUrl.trim()) {
    return '';
  }

  try {
    const parsedUrl = new URL(resetUrl, window.location.origin);
    const token = parsedUrl.pathname.split('/').filter(Boolean).pop();

    if (!token) {
      return '';
    }

    return buildAuthHref(
      `/reset-password/${encodeURIComponent(token)}`,
      redirectTarget,
    );
  } catch {
    return '';
  }
}

export default function ForgotPasswordForm({
  redirectTarget = '/account',
  messages = {},
}) {
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resetPath, setResetPath] = useState('');
  const [isPending, startTransition] = useTransition();
  const forgotMessage = (path, fallback = '') =>
    readForgotPasswordMessage(messages, path, fallback);
  const forgotErrors = {
    generic: forgotMessage(
      'errors.generic',
      'Unable to request a password reset.',
    ),
    tooManyAttempts: forgotMessage(
      'errors.tooManyAttempts',
      'Too many attempts. Please wait a moment and try again.',
    ),
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setResetPath('');

    startTransition(async () => {
      try {
        const response = await clientFetch('/api/users/password/forgot', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            email: email.trim().toLowerCase(),
          }),
        });

        setSuccessMessage(
          response?.message ||
            forgotMessage(
              'toast.sent',
              'If an account exists for that email, a reset link has been sent.',
            ),
        );
        setResetPath(buildLocalResetPath(response?.resetUrl, redirectTarget));
      } catch (error) {
        setErrorMessage(
          getSafeAuthClientMessage(error, {
            fallbackMessage: forgotErrors.generic,
            messages: forgotErrors,
          }),
        );
      }
    });
  };

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      <FormField
        label={forgotMessage('form.email.label', 'Email')}
        type="email"
        autoComplete="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        required
        placeholder={forgotMessage(
          'form.email.placeholder',
          'Enter your email',
        )}
      />

      {errorMessage ? (
        <div className="auth-message auth-message--error">{errorMessage}</div>
      ) : null}

      {successMessage ? (
        <div className="auth-message auth-message--success">
          <div>{successMessage}</div>
          {resetPath ? (
            <div>
              {forgotMessage('localDevHelper.prefix', 'Local dev helper:')}{' '}
              <Link href={resetPath}>
                {forgotMessage(
                  'localDevHelper.link',
                  'open the reset route in Next',
                )}
              </Link>
            </div>
          ) : null}
        </div>
      ) : null}

      <FormActions>
        <button
          type="submit"
          className="app-button app-button--primary"
          disabled={isPending}
        >
          {isPending
            ? forgotMessage('submitLoading', 'Sending Link...')
            : forgotMessage('submit', 'Send Reset Link')}
        </button>
        <Link
          href={buildAuthHref('/login', redirectTarget)}
          className="app-button app-button--ghost"
        >
          {forgotMessage('backToLogin', 'Back to sign in')}
        </Link>
      </FormActions>

      <p className="shop-panel__meta">
        {forgotMessage('backToRegisterPrompt', 'Need an account instead?')}{' '}
        <Link href={buildAuthHref('/register', redirectTarget)}>
          {forgotMessage('backToRegisterLink', 'Create one here')}
        </Link>
      </p>
    </form>
  );
}
